import { useState, useEffect } from 'react';
import { Save, Shield, Key, Mail, Phone, Building, Briefcase, Plus, Trash2, Copy } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { Link } from 'react-router-dom';
import { profileService } from '../services';
import type { Profile } from '../services';
import { useToast } from '../components/common/ToastContainer';
import userSshKeysService, { type UserSshKey, type IssuedSshCert } from '../services/userSshKeys.service';
import { Ban } from 'lucide-react';

const UserProfile = () => {
  const { user } = useAuthStore();
  const toast = useToast();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    phoneNumber: '',
    department: '',
    jobTitle: '',
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const profileData = await profileService.getProfile();
        setProfile(profileData);
        setFormData({
          email: profileData.email || '',
          fullName: profileData.fullName || '',
          phoneNumber: profileData.phoneNumber || '',
          department: profileData.department || '',
          jobTitle: profileData.jobTitle || '',
        });
      } catch (err) {
        console.error('Failed to load profile:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const updatedProfile = await profileService.updateProfile(formData);
      setProfile(updatedProfile);
    } catch (err: any) {
      toast.error('Update Failed', `Failed to update profile: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading profile...</div>;
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
          <p className="text-gray-600 mt-1">View and update your personal information</p>
        </div>
        <div className="flex items-center space-x-3">
          <Link to="/profile/settings" className="btn-secondary">
            Account Settings
          </Link>
          <button 
            onClick={handleSave} 
            disabled={saving}
            className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>

      {/* Personal Information */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField 
            label="Username" 
            value={profile?.username || user?.username || ''} 
            disabled 
            icon={Key} 
          />
          <FormField
            label="Email"
            value={formData.email}
            disabled
            icon={Mail}
          />
          <FormField 
            label="Full Name" 
            value={formData.fullName}
            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
          />
          <FormField 
            label="Phone Number" 
            value={formData.phoneNumber}
            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
            icon={Phone} 
          />
          <FormField 
            label="Department" 
            value={formData.department}
            onChange={(e) => setFormData({ ...formData, department: e.target.value })}
            icon={Building} 
          />
          <FormField 
            label="Job Title" 
            value={formData.jobTitle}
            onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
            icon={Briefcase} 
          />
        </div>
      </div>

      {/* Account Details */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoField icon={Shield} label="Role" value={profile?.role || 'N/A'} />
          <InfoField icon={Shield} label="Tenant" value={profile?.tenant || user?.tenantName || 'Default'} />
          <InfoField icon={Key} label="User ID" value={profile?.userId || profile?.id || user?.userId || 'N/A'} />
          <InfoField icon={Key} label="Last Login" value={profile?.lastLogin || 'N/A'} />
        </div>
      </div>

      {/* SSH Keys (VSNP-680) */}
      <SshKeysCard />

      {/* Issued SSH Certificates (VSNP-685 / workstream A) */}
      <IssuedSshCertsCard />

      {/* Quick Links */}
      <div className="card bg-gray-50">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Links</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link 
            to="/profile/settings" 
            className="p-4 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-sm transition-all group"
          >
            <h3 className="text-sm font-semibold text-gray-900 group-hover:text-primary-600">Security</h3>
            <p className="text-xs text-gray-500 mt-1">Auth provider, sessions</p>
          </Link>
          <Link 
            to="/profile/settings" 
            className="p-4 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-sm transition-all group"
          >
            <h3 className="text-sm font-semibold text-gray-900 group-hover:text-primary-600">Preferences</h3>
            <p className="text-xs text-gray-500 mt-1">Language, timezone, theme</p>
          </Link>
          <Link 
            to="/profile/settings" 
            className="p-4 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-sm transition-all group"
          >
            <h3 className="text-sm font-semibold text-gray-900 group-hover:text-primary-600">Notifications</h3>
            <p className="text-xs text-gray-500 mt-1">Email alerts, reports</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

const FormField = ({ 
  label, 
  value, 
  disabled = false, 
  onChange,
  icon: Icon 
}: { 
  label: string; 
  value: string; 
  disabled?: boolean; 
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  icon?: typeof Mail;
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      )}
      <input 
        type="text" 
        value={value} 
        disabled={disabled} 
        onChange={onChange}
        className={`input-base w-full ${Icon ? 'pl-10' : ''} ${disabled ? 'bg-gray-50' : ''}`}
        readOnly={disabled}
      />
    </div>
  </div>
);

const InfoField = ({ icon: Icon, label, value }: { icon: typeof Shield; label: string; value: string }) => (
  <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
    <Icon className="w-5 h-5 text-gray-500 mt-0.5" />
    <div>
      <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">{label}</div>
      <div className="text-sm font-semibold text-gray-900 mt-0.5">{value}</div>
    </div>
  </div>
);

/**
 * SshKeysCard — VSNP-680 / re-arch C.
 * Per-user SSH public-key store. Saved keys are picked from a dropdown
 * on the edge SSH-cert page so the user doesn't paste their pubkey
 * every time. Multi-key (one per laptop), with auto-renew flag.
 */
const SshKeysCard = () => {
  const toast = useToast();
  const [keys, setKeys] = useState<UserSshKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newPubKey, setNewPubKey] = useState('');
  const [newAutoRenew, setNewAutoRenew] = useState(false);
  const [newPrivateKeyPath, setNewPrivateKeyPath] = useState('');
  const [pathEdited, setPathEdited] = useState(false);

  // Mirror of backend defaultPrivateKeyPath — keep in sync.
  const inferPath = (pubKey: string): string => {
    const algo = pubKey.trim().split(/\s+/)[0] || '';
    if (algo === 'ssh-ed25519') return '~/.ssh/id_ed25519';
    if (algo === 'ssh-rsa') return '~/.ssh/id_rsa';
    if (algo.startsWith('ecdsa-sha2-')) return '~/.ssh/id_ecdsa';
    if (algo === 'ssh-dss') return '~/.ssh/id_dsa';
    return '~/.ssh/id_ed25519';
  };

  const onPubKeyChange = (val: string) => {
    setNewPubKey(val);
    if (!pathEdited) setNewPrivateKeyPath(inferPath(val));
  };

  const refresh = async () => {
    try {
      setLoading(true);
      setKeys(await userSshKeysService.list());
    } catch (e: any) {
      toast.error('Failed to load SSH keys', e.message || String(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const handleAdd = async () => {
    if (!newLabel.trim() || !newPubKey.trim()) {
      toast.error('Missing fields', 'Both label and public key are required');
      return;
    }
    try {
      setAdding(true);
      const path = newPrivateKeyPath.trim() || undefined;
      await userSshKeysService.add(newLabel.trim(), newPubKey.trim(), newAutoRenew, path);
      toast.success('SSH key added', `Saved as "${newLabel.trim()}"`);
      setNewLabel('');
      setNewPubKey('');
      setNewAutoRenew(false);
      setNewPrivateKeyPath('');
      setPathEdited(false);
      setShowAdd(false);
      await refresh();
    } catch (e: any) {
      toast.error('Failed to add key', e.message || e.response?.data?.error || String(e));
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: string, label: string) => {
    if (!confirm(`Remove SSH key "${label}"? Existing certs signed with it remain valid until expiry.`)) return;
    try {
      await userSshKeysService.remove(id);
      toast.success('SSH key removed', '');
      await refresh();
    } catch (e: any) {
      toast.error('Failed to remove key', e.message || String(e));
    }
  };

  const handleToggleAutoRenew = async (k: UserSshKey) => {
    try {
      await userSshKeysService.update(k.id, { autoRenew: !k.autoRenew });
      await refresh();
    } catch (e: any) {
      toast.error('Failed to update key', e.message || String(e));
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">SSH Keys</h2>
          <p className="text-sm text-gray-600 mt-1">
            Saved here once, used to sign SSH certificates for edge access without re-pasting each time.
          </p>
        </div>
        <button
          className="btn-secondary inline-flex items-center space-x-2"
          onClick={() => setShowAdd(s => !s)}
        >
          <Plus className="w-4 h-4" />
          <span>{showAdd ? 'Cancel' : 'Add Key'}</span>
        </button>
      </div>

      {showAdd && (
        <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50 space-y-3">
          <FormField
            label="Label (e.g. 'Macbook Pro 2025')"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Public key (single line — ssh-ed25519, ssh-rsa, or ecdsa-sha2-*)</label>
            <textarea
              value={newPubKey}
              onChange={(e) => onPubKeyChange(e.target.value)}
              placeholder="ssh-ed25519 AAAA... your-comment"
              rows={3}
              className="input-base w-full font-mono text-xs"
            />
            <p className="text-xs text-gray-500 mt-1">
              On Mac/Linux: list with <code className="bg-white px-1 rounded">ls ~/.ssh/*.pub</code> then print one with <code className="bg-white px-1 rounded">cat ~/.ssh/&lt;name&gt;.pub</code>. No keys? <code className="bg-white px-1 rounded">ssh-keygen -t ed25519</code>.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Private key file (on your machine)</label>
            <input
              type="text"
              value={newPrivateKeyPath}
              onChange={(e) => { setNewPrivateKeyPath(e.target.value); setPathEdited(true); }}
              placeholder="~/.ssh/id_ed25519"
              className="input-base w-full font-mono text-xs"
            />
            <p className="text-xs text-gray-500 mt-1">
              Used in the generated <code>~/.ssh/config</code> as <code>IdentityFile</code>. Auto-filled from the public key's algorithm — change only if your private key has a custom name.
            </p>
          </div>
          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input type="checkbox" checked={newAutoRenew} onChange={(e) => setNewAutoRenew(e.target.checked)} />
            <span>Auto-renew certificates signed with this key</span>
          </label>
          <div className="flex justify-end space-x-2">
            <button onClick={() => setShowAdd(false)} className="btn-secondary" disabled={adding}>Cancel</button>
            <button onClick={handleAdd} className="btn-primary" disabled={adding}>
              {adding ? 'Saving…' : 'Save key'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500">Loading SSH keys…</div>
      ) : keys.length === 0 ? (
        <div className="text-sm text-gray-500 italic">
          No SSH keys saved yet. Add one to skip pasting it on every cert generation.
        </div>
      ) : (
        <div className="divide-y divide-gray-200">
          {keys.map((k) => (
            <div key={k.id} className="py-3 flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-900">{k.label}</span>
                  {k.autoRenew && (
                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded">auto-renew</span>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-1 font-mono truncate" title={k.publicKey}>{k.publicKey}</div>
                <div className="text-xs text-gray-400 mt-1">
                  {k.fingerprint}
                  <> · <code className="font-mono">{k.privateKeyPath}</code></>
                  {!k.privateKeyPathExplicit && <span className="text-gray-300"> (auto)</span>}
                  {k.lastUsedAt && (
                    <> · last used {new Date(k.lastUsedAt).toLocaleString()}</>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2 ml-3 flex-shrink-0">
                <button
                  onClick={() => handleToggleAutoRenew(k)}
                  className="text-xs px-2 py-1 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                  title={k.autoRenew ? 'Disable auto-renew' : 'Enable auto-renew'}
                >
                  {k.autoRenew ? 'Disable auto-renew' : 'Enable auto-renew'}
                </button>
                <button
                  onClick={() => navigator.clipboard.writeText(k.publicKey)}
                  className="p-1.5 text-gray-500 border border-gray-300 rounded hover:bg-gray-50"
                  title="Copy public key"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(k.id, k.label)}
                  className="p-1.5 text-red-600 border border-red-200 rounded hover:bg-red-50"
                  title="Remove key"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * IssuedSshCertsCard — VSNP-685 / re-arch workstream A.
 * Lists this user's issued SSH user certs with sub-second revoke.
 * Backed by /api/v1/users/me/ssh-certs and POST .../revoke.
 */
const IssuedSshCertsCard = () => {
  const toast = useToast();
  const [certs, setCerts] = useState<IssuedSshCert[]>([]);
  const [loading, setLoading] = useState(true);
  const [revokingSerial, setRevokingSerial] = useState<string | null>(null);

  const refresh = async () => {
    try {
      setLoading(true);
      setCerts(await userSshKeysService.listIssuedCerts());
    } catch (e: any) {
      toast.error('Failed to load issued certs', e.message || String(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const handleRevoke = async (cert: IssuedSshCert) => {
    if (!confirm(`Revoke certificate ${cert.certSerial}?\n\nNext SSH attempt with this cert will be denied immediately. In-flight sessions are unaffected.`)) return;
    try {
      setRevokingSerial(cert.certSerial);
      await userSshKeysService.revokeCert(cert.certSerial, 'self-revoked from profile UI');
      toast.success('Certificate revoked', `Cert ${cert.certSerial.slice(-8)} can no longer be used.`);
      await refresh();
    } catch (e: any) {
      toast.error('Revoke failed', e.message || String(e));
    } finally {
      setRevokingSerial(null);
    }
  };

  const fmtTime = (s: string | null) => s ? new Date(s).toLocaleString() : '—';
  const fmtUnix = (s: number | null | undefined) => s ? new Date(s * 1000).toLocaleString() : '—';
  const isExpired = (cert: IssuedSshCert) => Math.floor(Date.now() / 1000) > cert.validBefore;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Issued SSH Certificates</h2>
          <p className="text-sm text-gray-600 mt-1">
            Sub-second revocation — clicking <strong>Revoke</strong> takes effect on the very next SSH attempt with this cert. In-flight sessions are unaffected.
          </p>
        </div>
        <button onClick={refresh} className="btn-secondary text-sm" disabled={loading}>Refresh</button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading certificates…</div>
      ) : certs.length === 0 ? (
        <div className="text-sm text-gray-500 italic">
          No SSH certificates issued for this account yet. Generate one from any edge's SSH Access tab.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs text-gray-600 uppercase">
                <th className="text-left py-2 pr-4">Issued</th>
                <th className="text-left py-2 pr-4">Serial</th>
                <th className="text-left py-2 pr-4">Edges</th>
                <th className="text-left py-2 pr-4">Expires</th>
                <th className="text-left py-2 pr-4">Status</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {certs.map((cert) => {
                const revoked = !!cert.revokedAt;
                const expired = !revoked && isExpired(cert);
                const statusBadge = revoked
                  ? <span className="text-xs px-2 py-0.5 bg-red-100 text-red-800 rounded">revoked</span>
                  : expired
                    ? <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">expired</span>
                    : <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded">active</span>;
                return (
                  <tr key={cert.certFingerprint} className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-gray-600">{fmtTime(cert.createdAt)}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{cert.certSerial.slice(-12)}</td>
                    <td className="py-2 pr-4 font-mono text-xs">
                      {cert.edgeIds && cert.edgeIds.length > 0
                        ? cert.edgeIds.map(g => g.slice(-8)).join(', ')
                        : <span className="text-gray-400">unscoped</span>}
                    </td>
                    <td className="py-2 pr-4 text-gray-600 text-xs">{fmtUnix(cert.validBefore)}</td>
                    <td className="py-2 pr-4">{statusBadge}{revoked && cert.revokeReason && <div className="text-xs text-gray-500 mt-1">{cert.revokeReason}</div>}</td>
                    <td className="py-2 text-right">
                      {!revoked && !expired && (
                        <button
                          onClick={() => handleRevoke(cert)}
                          disabled={revokingSerial === cert.certSerial}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-700 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50"
                          title="Revoke certificate"
                        >
                          <Ban className="w-3 h-3" />
                          {revokingSerial === cert.certSerial ? 'Revoking…' : 'Revoke'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
